import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  ScheduleComponent,
  ViewsDirective,
  ViewDirective,
  ResourcesDirective,
  ResourceDirective,
  TimelineViews,
  TimelineMonth,
  TimelineYear,
  Resize,
  DragAndDrop,
  Inject,
  type ActionEventArgs,
  type EventClickArgs,
  type NavigatingEventArgs,
  type RenderCellEventArgs,
  type EventRenderedArgs,
  type PopupOpenEventArgs,
} from '@syncfusion/ej2-react-schedule';
import type { ScheduleItem, ViewMode } from '../../stores/scheduleStore';
import { useScheduleStore } from '../../stores/scheduleStore';
import { useWorkloadStore, type WorkloadEntry } from '../../stores/workloadStore';
import type { LeaveEntry } from '../../stores/leaveStore';
import type { UserHoliday } from '../../hooks/useHolidays';
import { updateItemTimeline, updateItemAssignee } from '../../api/schedule.api';
import { getFieldMappingLocal, getSubitemFieldMappingLocal, getPerPersonDistributionLocal, getShowParentItemNameLocal } from '../../api/board-config.api';
import { buildReassignedEntries, redistributeHours, syncSiblingTimelines } from '../../utils/assignee-helpers';
import { getWorkloadCellDisplay, type WorkloadCellInput } from '../../utils/workload-display';
import { computeRemainingPerPerson, isItemOverlogged } from '../../services/workload-data.service';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';
import CapacityDetailPopup, { aggregateByDay, aggregateByMonth, type AggregatedEntry } from './CapacityDetailPopup';
import { format, parseISO, getDay } from 'date-fns';
import type { DaySchedule } from '../../api/capacity.api';
import './syncfusion-overrides.css';

// Prevent Syncfusion from entering adaptive/mobile layout on iPad/tablets.
// When isAdaptive is true, the scheduler shows only one resource at a time
// with a breadcrumb navigation instead of the normal multi-row timeline.
// Syncfusion's Browser.isDevice is a getter that re-detects iPad on every call,
// so we must replace the getter itself.
import { Browser } from '@syncfusion/ej2-base';
Object.defineProperty(Browser, 'isDevice', { get: () => false, configurable: true });

// getDay() returns 0=Sun … 6=Sat → DaySchedule key
const DOW_TO_KEY: (keyof DaySchedule)[] = [
  'sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat',
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserInfo {
  id: number;
  name: string;
  avatarUrl?: string;
}

export interface TeamInfo {
  id: number;
  name: string;
}

import type { GroupByMode } from '../../stores/filterStore';
export type { GroupByMode } from '../../stores/filterStore';

export interface SyncfusionSchedulerProps {
  boardId: number;
  items: ScheduleItem[];
  userMap: Map<number, UserInfo>;
  viewMode: ViewMode;
  dateRange: { start: string; end: string };
  holidays?: Map<string, string>;
  /** Holiday entries with per-user assignments — rendered as non-interactive events */
  userHolidays?: UserHoliday[];
  /** Current grouping mode */
  groupByMode?: GroupByMode;
  /** When teams mode — which teams are selected */
  selectedTeamIds?: number[];
  /** All available teams with their member user IDs */
  teamMemberMap?: Map<number, { team: TeamInfo; memberIds: number[] }>;
  /** Leave entries to render as background hatched events */
  leaves?: LeaveEntry[];
  /** Current monday.com user id — used for owner-based permission checks */
  currentUserId?: number;
  onTaskClick?: (taskId: string, position?: { x: number; y: number }) => void;
  /** Called when a leave event is clicked — opens leave tooltip/detail */
  onLeaveClick?: (leaveId: string, position?: { x: number; y: number }) => void;
  onDateRangeChange?: (start: string, end: string) => void;
  /**
   * Called when an empty scheduler cell is single-clicked. Parent uses this to
   * open a small context menu (CellContextMenu) anchored at the click coords.
   * `x` / `y` are viewport coordinates from the native click event.
   */
  onCellClick?: (
    date: Date,
    assigneeId: number,
    endDate: Date | undefined,
    assigneeKind: 'person' | 'team',
    x: number,
    y: number,
  ) => void;
  /**
   * Called when an external item (from search panel) is dropped onto the scheduler.
   * `assigneeKind` is 'team' when dropped on a team row in teams-only mode.
   */
  onExternalDrop?: (mondayItemId: number, date: Date, assigneeId: number, assigneeKind?: 'person' | 'team') => void;
  /** Called when the owner resizes a pending leave item on the scheduler */
  onLeaveResized?: (leaveId: string, newStart: string, newEnd: string) => void;
  /** Worklog events to display as items on the scheduler (worklog_workload mode) */
  worklogEvents?: WorklogEvent[];
  /** Called when a worklog event is clicked */
  onWorklogClick?: (worklogId: string, position?: { x: number; y: number }) => void;
}

/** Worklog event data shape for rendering on the scheduler */
export interface WorklogEvent {
  id: string;
  mondayItemId: string;
  mondayBoardId: string;
  mondayUserId: number;
  date: string;
  hours: number;
  description: string | null;
  itemName?: string;
}

/** Imperative handle exposed to parent via ref */
export interface SyncfusionSchedulerHandle {
  clearSelection: () => void;
}

// ─── Syncfusion view mapping ──────────────────────────────────────────────────

const VIEW_MODE_MAP: Record<ViewMode, string> = {
  'week-daily': 'TimelineWeek',
  '2weeks-daily': 'TimelineWeek',
  'month-daily': 'TimelineMonth',
  'year-monthly': 'TimelineYear',
  'year-daily': 'TimelineMonth',
};

/** Header row configuration per view mode for Syncfusion timeline views */
function getHeaderRowsForMode(mode: ViewMode) {
  switch (mode) {
    case 'week-daily':
    case '2weeks-daily':
      return [{ option: 'Date' as const }];
    case 'month-daily':
      return [{ option: 'Date' as const }];
    case 'year-monthly':
      return [{ option: 'Year' as const }, { option: 'Month' as const }];
    case 'year-daily':
      return [{ option: 'Month' as const }, { option: 'Date' as const }];
  }
}

// ─── Data conversion ──────────────────────────────────────────────────────────

function toSyncfusionEvents(
  items: ScheduleItem[],
  userTeamMap?: Map<number, number[]>,
) {
  const events: Record<string, unknown>[] = [];

  for (const item of items) {
    // Use start-of-day for StartTime and end-of-day for EndTime.
    // This avoids the all-day +1 day rendering issue while still
    // spanning the correct visual range in timeline views.
    const start = new Date(item.startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(item.endDate);
    end.setHours(23, 59, 59, 0);

    const base = {
      Subject: item.name,
      StartTime: start,
      EndTime: end,
      IsAllDay: true,
      AssigneeId: item.assigneeId,
      Color: item.color || '#579bfc',
      Hours: item.hours,
      WorkHourFormat: item.workHourFormat,
      HasHoursConflict: item.hasHoursConflict,
      MondayItemId: item.mondayItemId,
      BoardId: item.boardId,
      GroupId: item.groupId,
      Status: item.status,
      StatusColor: item.statusColor,
      IsSubitem: item.isSubitem || false,
      SubitemsBoardId: item.subitemsBoardId,
      ParentItemName: item.parentItemName,
      OriginalTotalHours: item.originalTotalHours ?? item.hours,
      AssigneeCount: item.assigneeCount ?? 1,
    };

    // For team-assigned items (assignedTeamId set, assigneeId=0), use assignedTeamId as TeamId.
    if (item.assignedTeamId) {
      events.push({ ...base, Id: item.id, TeamId: item.assignedTeamId });
      continue;
    }

    // For person-assigned items, look up all teams via the user→teams mapping.
    // If the user belongs to multiple selected teams, duplicate the event for each team
    // so it appears under every team the user belongs to.
    const teamIds = userTeamMap?.get(item.assigneeId);
    if (teamIds && teamIds.length > 1) {
      for (const teamId of teamIds) {
        events.push({ ...base, Id: `${item.id}_t${teamId}`, TeamId: teamId });
      }
    } else {
      events.push({ ...base, Id: item.id, TeamId: teamIds?.[0] });
    }
  }

  return events;
}

/** Convert worklog events to Syncfusion-compatible events */
function toWorklogSyncfusionEvents(
  worklogs: WorklogEvent[],
  userTeamMap?: Map<number, number[]>,
): Record<string, unknown>[] {
  const events: Record<string, unknown>[] = [];
  for (const wl of worklogs) {
    const date = new Date(wl.date);
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(date);
    end.setHours(23, 59, 59, 0);

    const base = {
      Subject: wl.itemName || '',
      StartTime: start,
      EndTime: end,
      IsAllDay: true,
      AssigneeId: wl.mondayUserId,
      Color: '#676879', // Gray accent for worklog events
      IsWorklog: true,
      WorklogId: wl.id,
      MondayItemId: wl.mondayItemId,
      BoardId: wl.mondayBoardId,
      Hours: wl.hours,
      Description: wl.description,
      IsReadonly: true, // Prevent drag/resize
    };

    const teamIds = userTeamMap?.get(wl.mondayUserId);
    if (teamIds && teamIds.length > 1) {
      for (const teamId of teamIds) {
        events.push({ ...base, Id: `wl_${wl.id}_t${teamId}`, TeamId: teamId });
      }
    } else {
      events.push({ ...base, Id: `wl_${wl.id}`, TeamId: teamIds?.[0] });
    }
  }
  return events;
}

type AssigneeResource = {
  text: string;
  id: number;
  color: string;
  avatar: string | null;
  teamId?: number;
};

type TeamResource = {
  text: string;
  id: number;
  color: string;
};

function toSyncfusionResources(
  userMap: Map<number, UserInfo>,
) {
  // Show ALL users as resource rows (not just those with items).
  // Skip id=0 (Unassigned) — items with assigneeId=0 will simply not appear.
  const resources: AssigneeResource[] = [];

  for (const [userId, user] of userMap) {
    if (userId === 0) continue;
    resources.push({
      text: user.name,
      id: userId,
      color: '#579bfc',
      avatar: user.avatarUrl ?? null,
    });
  }

  return resources;
}

/**
 * Build resources for team mode — two-level: Teams → Assignees
 * Returns { teams, assignees } arrays for two ResourceDirective entries.
 */
function toTeamResources(
  items: ScheduleItem[],
  userMap: Map<number, UserInfo>,
  selectedTeamIds: number[],
  teamMemberMap: Map<number, { team: TeamInfo; memberIds: number[] }>,
) {
  const teams: TeamResource[] = [];
  const assignees: AssigneeResource[] = [];

  // Build a set of users that belong to selected teams
  const userToTeams = new Map<number, number[]>();
  for (const teamId of selectedTeamIds) {
    const entry = teamMemberMap.get(teamId);
    if (!entry) continue;
    teams.push({
      text: entry.team.name,
      id: entry.team.id,
      color: '#579bfc',
    });
    for (const memberId of entry.memberIds) {
      const existing = userToTeams.get(memberId) ?? [];
      existing.push(teamId);
      userToTeams.set(memberId, existing);
    }
  }

  // Build assignee resources with teamId for grouping
  const seenAssignees = new Set<string>();
  for (const [userId, teamIds] of userToTeams) {
    const user = userMap.get(userId);
    for (const teamId of teamIds) {
      const key = `${userId}_${teamId}`;
      if (seenAssignees.has(key)) continue;
      seenAssignees.add(key);
      assignees.push({
        text: user?.name ?? `User ${userId}`,
        id: userId,
        color: '#579bfc',
        avatar: user?.avatarUrl ?? null,
        teamId,
      });
    }
  }

  return { teams, assignees };
}

/**
 * Build resources for teams-only mode — single level: just team rows.
 * Items assigned to team members appear on the team row.
 */
function toTeamsOnlyResources(
  selectedTeamIds: number[],
  teamMemberMap: Map<number, { team: TeamInfo; memberIds: number[] }>,
) {
  const teams: TeamResource[] = [];
  for (const teamId of selectedTeamIds) {
    const entry = teamMemberMap.get(teamId);
    if (!entry) continue;
    teams.push({
      text: entry.team.name,
      id: entry.team.id,
      color: '#579bfc',
    });
  }
  return teams;
}

/**
 * Convert items so their AssigneeId points to the team ID (for teams-only mode).
 * Each item assigned to a team member gets remapped to that team's row.
 * If a user belongs to multiple selected teams, the event is duplicated for each.
 */
function toTeamsOnlyEvents(
  items: ScheduleItem[],
  selectedTeamIds: number[],
  _teamMemberMap: Map<number, { team: TeamInfo; memberIds: number[] }>,
) {
  const events: Record<string, unknown>[] = [];

  for (const item of items) {
    // In teams-only mode, show ONLY items that are directly assigned to a team
    if (!item.assignedTeamId || !selectedTeamIds.includes(item.assignedTeamId)) {
      continue;
    }

    const start = new Date(item.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(item.endDate);
    end.setHours(23, 59, 59, 0);

    events.push({
      Id: item.id,
      Subject: item.name,
      StartTime: start,
      EndTime: end,
      IsAllDay: true,
      Color: item.color || '#579bfc',
      Hours: item.hours,
      WorkHourFormat: item.workHourFormat,
      HasHoursConflict: item.hasHoursConflict,
      MondayItemId: item.mondayItemId,
      BoardId: item.boardId,
      GroupId: item.groupId,
      Status: item.status,
      StatusColor: item.statusColor,
      IsSubitem: item.isSubitem || false,
      SubitemsBoardId: item.subitemsBoardId,
      TeamRowId: item.assignedTeamId,
    });
  }

  return events;
}

/** Convert leave entries to Syncfusion "background" events with hatched styling */
function toLeaveEvents(
  leaves: LeaveEntry[],
  userTeamMap?: Map<number, number[]>,
) {
  const events: Record<string, unknown>[] = [];

  for (const leave of leaves) {
    const start = new Date(leave.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(leave.endDate);
    end.setHours(23, 59, 59, 0);

    const dayLabel =
      leave.dayType === 'full' ? 'Full day' : leave.dayType === 'am' ? 'AM' : 'PM';

    const base = {
      Subject: `${leave.leaveType} (${dayLabel})`,
      StartTime: start,
      EndTime: end,
      IsAllDay: true,
      AssigneeId: leave.assigneeId,
      IsLeave: true,
      LeaveType: leave.leaveType,
      LeaveStatus: leave.status,
      DayType: leave.dayType,
      Note: leave.note,
      Color: '#e6e9ef',
    };

    const teamIds = userTeamMap?.get(leave.assigneeId);
    if (teamIds && teamIds.length > 1) {
      for (const teamId of teamIds) {
        events.push({ ...base, Id: `${leave.id}_t${teamId}`, TeamId: teamId });
      }
    } else {
      events.push({ ...base, Id: leave.id, TeamId: teamIds?.[0] });
    }
  }

  return events;
}

/** Convert user holidays to non-interactive Syncfusion events.
 *  Each holiday appears once per assigned user on their resource row. */
function toHolidayEvents(
  userHolidays: UserHoliday[],
  userTeamMap?: Map<number, number[]>,
) {
  const events: Record<string, unknown>[] = [];

  for (const holiday of userHolidays) {
    const date = new Date(holiday.date);
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(date);
    end.setHours(23, 59, 59, 0);

    for (const userIdStr of holiday.assignedUserIds) {
      const userId = Number(userIdStr);
      if (!userId) continue;

      const base = {
        Subject: holiday.name,
        StartTime: start,
        EndTime: end,
        IsAllDay: true,
        AssigneeId: userId,
        IsHoliday: true,
        Color: '#fff3e0',
      };

      const teamIds = userTeamMap?.get(userId);
      if (teamIds && teamIds.length > 1) {
        for (const teamId of teamIds) {
          events.push({
            ...base,
            Id: `holiday_${holiday.date}_${userId}_t${teamId}`,
            TeamId: teamId,
          });
        }
      } else {
        events.push({
          ...base,
          Id: `holiday_${holiday.date}_${userId}`,
          TeamId: teamIds?.[0],
        });
      }
    }
  }

  return events;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SyncfusionScheduler = React.forwardRef<SyncfusionSchedulerHandle, SyncfusionSchedulerProps>(({
  boardId,
  items,
  userMap,
  viewMode,
  dateRange,
  holidays,
  userHolidays = [],
  groupByMode = 'assignees',
  selectedTeamIds = [],
  teamMemberMap,
  leaves = [],
  currentUserId,
  onTaskClick,
  onLeaveClick,
  onDateRangeChange,
  onCellClick,
  onExternalDrop,
  onLeaveResized,
  worklogEvents: worklogEventsProp,
  onWorklogClick,
}, ref) => {
  const scheduleRef = useRef<ScheduleComponent>(null);
  const firstDayOfWeek = useScheduleStore((s) => s.firstDayOfWeek);
  const hideWeekends = useScheduleStore((s) => s.hideWeekends);

  // Expose imperative methods to parent
  useImperativeHandle(ref, () => ({
    clearSelection: () => {
      // Remove Syncfusion's .e-selected-cell from all cells
      const el = scheduleRef.current?.element;
      if (el) {
        el.querySelectorAll('.e-selected-cell').forEach((cell) => {
          cell.classList.remove('e-selected-cell');
        });
      }
    },
  }), []);

  // Track whether an event drag is in progress so onSelect doesn't fire mid-drag
  const isDraggingEventRef = useRef(false);
  // Skip the next `events` useMemo recomputation after a drag/resize so Syncfusion
  // keeps its already-correct visual state and doesn't blink all events.
  const skipNextEventsUpdateRef = useRef(false);
  const prevEventsRef = useRef<Record<string, unknown>[]>([]);

  // Track modifier key state for add-assignee drag mode (Ctrl/Cmd+drag = split/add).
  // Use multiple event sources for reliability across dev and prod builds:
  // 1. Capture-phase mouseup reads ctrlKey/metaKey right at drop time.
  // 2. keydown/keyup track the key state continuously as a fallback in case
  //    Syncfusion's internal handlers interfere with mouseup event properties.
  const modifierKeyRef = useRef(false);
  useEffect(() => {
    const onMouseUp = (e: MouseEvent) => {
      modifierKeyRef.current = e.ctrlKey || e.metaKey;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        modifierKeyRef.current = true;
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        modifierKeyRef.current = false;
      }
    };
    // Use capture phase to read the modifier before Syncfusion handles the drop
    window.addEventListener('mouseup', onMouseUp, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('mouseup', onMouseUp, true);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
    };
  }, []);

  // ── Workload state ────────────────────────────────────────────────────────
  const {
    enabled: workloadEnabled,
    indicatorType,
    displayMode: workloadDisplayMode,
    entries: workloadEntries,
    issueCountEntries,
    defaultSchedule,
    userSchedules,
    worklogsByItem,
  } = useWorkloadStore();

  // ── Refs for volatile workload data ────────────────────────────────────────
  // Callbacks passed to Syncfusion (renderCell, resourceHeaderTemplate, etc.)
  // read from these refs so their function references stay stable. This prevents
  // Syncfusion from re-rendering all events when workload data changes.
  const workloadEnabledRef = useRef(workloadEnabled);
  workloadEnabledRef.current = workloadEnabled;
  const workloadDisplayModeRef = useRef(workloadDisplayMode);
  workloadDisplayModeRef.current = workloadDisplayMode;
  const indicatorTypeRef = useRef(indicatorType);
  indicatorTypeRef.current = indicatorType;
  const workloadEntriesRef = useRef(workloadEntries);
  workloadEntriesRef.current = workloadEntries;
  const issueCountEntriesRef = useRef(issueCountEntries);
  issueCountEntriesRef.current = issueCountEntries;
  const defaultScheduleRef = useRef(defaultSchedule);
  defaultScheduleRef.current = defaultSchedule;
  const userSchedulesRef = useRef(userSchedules);
  userSchedulesRef.current = userSchedules;
  const worklogsByItemRef = useRef(worklogsByItem);
  worklogsByItemRef.current = worklogsByItem;
  const holidaysRef = useRef(holidays);
  holidaysRef.current = holidays;
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;

  // Capacity detail popup state (shown on workload bar hover)
  const [capacityPopup, setCapacityPopup] = useState<{
    userName: string;
    aggregatedEntries: AggregatedEntry[];
    anchorRect: { top: number; left: number };
  } | null>(null);
  const capacityPopupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showCapacityPopup = useCallback((data: NonNullable<typeof capacityPopup>) => {
    if (capacityPopupTimerRef.current) {
      clearTimeout(capacityPopupTimerRef.current);
      capacityPopupTimerRef.current = null;
    }
    setCapacityPopup(data);
  }, []);

  const hideCapacityPopup = useCallback(() => {
    capacityPopupTimerRef.current = setTimeout(() => {
      setCapacityPopup(null);
      capacityPopupTimerRef.current = null;
    }, 150);
  }, []);

  const cancelHideCapacityPopup = useCallback(() => {
    if (capacityPopupTimerRef.current) {
      clearTimeout(capacityPopupTimerRef.current);
      capacityPopupTimerRef.current = null;
    }
  }, []);

  // 'teams' = team rows only; 'teamWithUsers' = team rows + user rows underneath
  const isTeamsOnly = groupByMode === 'teams' && selectedTeamIds.length > 0 && !!teamMemberMap;
  const isTeamWithUsers = groupByMode === 'teamWithUsers' && selectedTeamIds.length > 0 && !!teamMemberMap;
  const isTeamMode = isTeamsOnly || isTeamWithUsers;

  // NOTE: Team-based item filtering is handled by SchedulerBoard (filteredItems prop).
  // SyncfusionScheduler receives already-filtered items — no duplicate filtering needed here.

  // In team mode, build a user→teams mapping for event grouping.
  // A user who belongs to multiple selected teams maps to ALL of them,
  // so their items appear under each team they belong to.
  const userTeamMapping = useMemo(() => {
    if (!isTeamMode || !teamMemberMap) return undefined;
    const map = new Map<number, number[]>();
    for (const teamId of selectedTeamIds) {
      const entry = teamMemberMap.get(teamId);
      if (entry) {
        for (const memberId of entry.memberIds) {
          const existing = map.get(memberId);
          if (existing) {
            existing.push(teamId);
          } else {
            map.set(memberId, [teamId]);
          }
        }
      }
    }
    return map;
  }, [isTeamMode, selectedTeamIds, teamMemberMap]);

  const events = useMemo(() => {
    // After a drag/resize, Syncfusion already has the correct visual state.
    // Skip this recomputation to avoid a full dataSource re-render blink.
    if (skipNextEventsUpdateRef.current) {
      skipNextEventsUpdateRef.current = false;
      return prevEventsRef.current;
    }

    if (isTeamsOnly && teamMemberMap) {
      // Teams-only: remap items to team rows
      const taskEvents = toTeamsOnlyEvents(items, selectedTeamIds, teamMemberMap);
      // Leaves also go to team rows — duplicate for users in multiple teams
      const leaveTeamEvents: Record<string, unknown>[] = [];
      for (const leave of leaves) {
        const start = new Date(leave.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(leave.endDate);
        end.setHours(23, 59, 59, 0);
        const dayLabel = leave.dayType === 'full' ? 'Full day' : leave.dayType === 'am' ? 'AM' : 'PM';
        const base = {
          Subject: `${leave.leaveType} (${dayLabel})`,
          StartTime: start,
          EndTime: end,
          IsAllDay: true,
          IsLeave: true,
          LeaveType: leave.leaveType,
          LeaveStatus: leave.status,
          DayType: leave.dayType,
          Note: leave.note,
          Color: '#e6e9ef',
        };
        // Find all teams for this user
        const matchingTeams: number[] = [];
        for (const teamId of selectedTeamIds) {
          const entry = teamMemberMap.get(teamId);
          if (entry && entry.memberIds.includes(leave.assigneeId)) {
            matchingTeams.push(teamId);
          }
        }
        if (matchingTeams.length > 1) {
          for (const teamId of matchingTeams) {
            leaveTeamEvents.push({ ...base, Id: `${leave.id}_t${teamId}`, TeamRowId: teamId });
          }
        } else {
          leaveTeamEvents.push({ ...base, Id: leave.id, TeamRowId: matchingTeams[0] });
        }
      }

      // Holiday events — remap to team rows
      const holidayTeamEvents: Record<string, unknown>[] = [];
      for (const holiday of userHolidays) {
        const hDate = new Date(holiday.date);
        const hStart = new Date(hDate);
        hStart.setHours(0, 0, 0, 0);
        const hEnd = new Date(hDate);
        hEnd.setHours(23, 59, 59, 0);

        // Find all teams that contain any of the holiday's assigned users
        const teamsForHoliday = new Set<number>();
        for (const userIdStr of holiday.assignedUserIds) {
          const userId = Number(userIdStr);
          for (const teamId of selectedTeamIds) {
            const entry = teamMemberMap.get(teamId);
            if (entry && entry.memberIds.includes(userId)) {
              teamsForHoliday.add(teamId);
            }
          }
        }
        for (const teamId of teamsForHoliday) {
          holidayTeamEvents.push({
            Subject: holiday.name,
            StartTime: hStart,
            EndTime: hEnd,
            IsAllDay: true,
            IsHoliday: true,
            Color: '#fff3e0',
            Id: `holiday_${holiday.date}_team${teamId}`,
            TeamRowId: teamId,
          });
        }
      }

      const result = [...taskEvents, ...leaveTeamEvents, ...holidayTeamEvents];
      prevEventsRef.current = result;
      return result;
    }

    const taskEvents = toSyncfusionEvents(items, userTeamMapping);
    const leaveEvents = toLeaveEvents(leaves, userTeamMapping);
    const holidayEvents = toHolidayEvents(userHolidays, userTeamMapping);
    const wlEvents = worklogEventsProp ? toWorklogSyncfusionEvents(worklogEventsProp, userTeamMapping) : [];
    const result = [...taskEvents, ...leaveEvents, ...holidayEvents, ...wlEvents];
    prevEventsRef.current = result;
    return result;
  }, [items, userTeamMapping, leaves, userHolidays, isTeamsOnly, teamMemberMap, selectedTeamIds, worklogEventsProp]);

  // Assignee-mode flat resources — show all users, not just those with items
  const assigneeResources = useMemo(
    () => toSyncfusionResources(userMap),
    [userMap],
  );

  // Teams-only mode resources — flat team rows
  const teamsOnlyResources = useMemo(() => {
    if (!isTeamsOnly || !teamMemberMap) return null;
    return toTeamsOnlyResources(selectedTeamIds, teamMemberMap);
  }, [isTeamsOnly, selectedTeamIds, teamMemberMap]);

  // TeamWithUsers mode — two-level hierarchy: Teams → Assignees
  const teamWithUsersResources = useMemo(() => {
    if (!isTeamWithUsers || !teamMemberMap) return null;
    return toTeamResources(items, userMap, selectedTeamIds, teamMemberMap);
  }, [items, userMap, selectedTeamIds, teamMemberMap, isTeamWithUsers]);

  // For popup/external-drop resolution, keep a flat list of the currently visible assignees
  const resources = isTeamWithUsers && teamWithUsersResources
    ? teamWithUsersResources.assignees
    : isTeamsOnly && teamsOnlyResources
      ? teamsOnlyResources.map((t) => ({ ...t, avatar: null as string | null }))
      : assigneeResources;

  const selectedDate = useMemo(
    () => new Date(dateRange.start),
    [dateRange.start],
  );

  const currentViewName = VIEW_MODE_MAP[viewMode] || 'TimelineWeek';

  // ── External drag support ──────────────────────────────────────────────────
  // Uses Syncfusion's getCellDetails + getResourcesByIndex for reliable
  // cell date and resource resolution (same pattern as the official
  // Syncfusion external-drag-drop demo).

  const resourcesRef = useRef(resources);
  resourcesRef.current = resources;

  const isTeamsOnlyRef = useRef(isTeamsOnly);
  isTeamsOnlyRef.current = isTeamsOnly;

  const isTeamWithUsersRef = useRef(isTeamWithUsers);
  isTeamWithUsersRef.current = isTeamWithUsers;

  const onExternalDropRef = useRef(onExternalDrop);
  onExternalDropRef.current = onExternalDrop;

  useEffect(() => {
    const schedulerEl = scheduleRef.current?.element;
    if (!schedulerEl) return;

    let lastHoveredCell: HTMLElement | null = null;

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }

      // Highlight the cell under the cursor
      const cell = (e.target as HTMLElement).closest('.e-work-cells') as HTMLElement | null;
      if (cell !== lastHoveredCell) {
        if (lastHoveredCell) lastHoveredCell.classList.remove('rp-drag-hover');
        if (cell) cell.classList.add('rp-drag-hover');
        lastHoveredCell = cell;
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      // Only clear highlight when leaving the scheduler entirely
      const related = e.relatedTarget as HTMLElement | null;
      if (!related || !schedulerEl.contains(related)) {
        if (lastHoveredCell) {
          lastHoveredCell.classList.remove('rp-drag-hover');
          lastHoveredCell = null;
        }
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();

      // Clear hover highlight
      if (lastHoveredCell) {
        lastHoveredCell.classList.remove('rp-drag-hover');
        lastHoveredCell = null;
      }

      if (!e.dataTransfer || !onExternalDropRef.current) return;

      try {
        const raw = e.dataTransfer.getData('application/rp-item');
        if (!raw) return;
        const data = JSON.parse(raw) as { mondayItemId: number; name: string };

        const schedule = scheduleRef.current;
        if (!schedule) return;

        // Find the closest work cell to the drop point
        const targetCell = (e.target as HTMLElement).closest('.e-work-cells') as HTMLElement;
        if (!targetCell) return;

        // Use Syncfusion's getCellDetails to get cell date
        const cellData = schedule.getCellDetails(targetCell);
        if (!cellData) return;
        const dropDate = cellData.startTime as Date;

        // Resolve which resource row the item was dropped on.
        // Use the data-group-index attribute Syncfusion puts on each .e-work-cells element.
        let assigneeId = 0;
        const attr = targetCell.getAttribute('data-group-index');
        if (attr != null) {
          const groupIdx = parseInt(attr, 10);
          // Try Syncfusion's getResourcesByIndex first (returns actual resource data)
          try {
            const resourceDetails = schedule.getResourcesByIndex(groupIdx);
            if (resourceDetails?.resourceData) {
              const rd = resourceDetails.resourceData as Record<string, unknown>;
              // Resource objects use lowercase 'id' (matching idField="id" in ResourceDirective)
              assigneeId = (rd.id as number) ?? 0;
            }
          } catch {
            // Fallback: index directly into our resources array
            assigneeId = resourcesRef.current[groupIdx]?.id ?? 0;
          }
        }

        const assigneeKind: 'person' | 'team' = isTeamsOnlyRef.current ? 'team' : 'person';

        onExternalDropRef.current(data.mondayItemId, dropDate, assigneeId, assigneeKind);
      } catch {
        // silently ignore parse errors
      }
    };

    schedulerEl.addEventListener('dragover', handleDragOver);
    schedulerEl.addEventListener('dragleave', handleDragLeave);
    schedulerEl.addEventListener('drop', handleDrop);

    return () => {
      schedulerEl.removeEventListener('dragover', handleDragOver);
      schedulerEl.removeEventListener('dragleave', handleDragLeave);
      schedulerEl.removeEventListener('drop', handleDrop);
    };
  }, []);

  // ── Event handlers ──────────────────────────────────────────────────────────

  /** Check if a leave event can be resized: only pending leaves owned by the current user */
  const isResizableLeave = useCallback(
    (evt: Record<string, unknown>) =>
      evt.IsLeave === true &&
      evt.LeaveStatus === 'Pending' &&
      currentUserId != null &&
      Number(evt.AssigneeId) === currentUserId,
    [currentUserId],
  );

  /** Prevent drag and resize on leave/holiday events — except pending leaves owned by current user (resize only) */
  const onActionBegin = useCallback(
    (args: ActionEventArgs) => {
      const type = args.requestType;
      if (type === 'eventChange' || type === 'eventDrag' || type === 'eventResize') {
        const data = args.data as Record<string, unknown> | Record<string, unknown>[] | undefined;
        if (!data) return;
        const events = Array.isArray(data) ? data : [data];
        for (const evt of events) {
          // Holiday and worklog events are always non-interactive
          if (evt.IsHoliday || evt.IsWorklog) {
            args.cancel = true;
            return;
          }
          if (evt.IsLeave) {
            // Allow resize for pending leaves owned by current user
            if (type === 'eventResize' && isResizableLeave(evt)) continue;
            if (type === 'eventChange' && isResizableLeave(evt)) continue;
            args.cancel = true;
            return;
          }
        }
      }
    },
    [isResizableLeave],
  );

  /** Cancel drag start on leave events — dragging leaves is always blocked. */
  const onDragStart = useCallback(
    (args: Record<string, unknown>) => {
      const data = args.data as Record<string, unknown> | undefined;
      if (data?.IsLeave || data?.IsHoliday || data?.IsWorklog) {
        args.cancel = true;
        return;
      }

      isDraggingEventRef.current = true;

      const cleanup = () => {
        isDraggingEventRef.current = false;
        document.removeEventListener('mouseup', cleanup);
      };
      document.addEventListener('mouseup', cleanup, { once: true });
    },
    [],
  );

  /** Cancel resize start on leave/holiday events — allow pending leaves owned by current user.
   *  Also disable resize entirely in year-monthly view where slots are full months
   *  and Syncfusion's resize rendering produces broken visuals. */
  const onResizeStart = useCallback(
    (args: Record<string, unknown>) => {
      // Year-monthly (TimelineYear): month-granularity resize is impractical
      // and causes severe visual artifacts (events stretch across rows).
      if (viewModeRef.current === 'year-monthly') {
        args.cancel = true;
        return;
      }
      const data = args.data as Record<string, unknown> | undefined;
      if (data?.IsHoliday || data?.IsWorklog) {
        args.cancel = true;
        return;
      }
      if (data?.IsLeave && !isResizableLeave(data)) {
        args.cancel = true;
      }
    },
    [isResizableLeave],
  );

  /** Fix off-by-one resize in TimelineMonth views.
   *
   *  Events use EndTime = 23:59:59 (end-of-day) so they render at the correct
   *  visual width. But Syncfusion's TimelineMonth resize snapping treats 23:59:59
   *  as occupying that day's full slot, so shrinking from the right is always
   *  one day too long. We detect right-edge resize and subtract one day from the
   *  result before actionComplete processes it.
   *
   *  In week/2-week views the slots are fine-grained enough that this doesn't
   *  manifest, but in month-daily and year-daily (both TimelineMonth) it's
   *  consistently off by one. */
  const onResizeStop = useCallback(
    (args: Record<string, unknown>) => {
      const vm = viewModeRef.current;
      if (vm !== 'month-daily' && vm !== 'year-daily') return;

      const data = args.data as Record<string, unknown> | undefined;
      if (!data) return;

      const startTime = data.StartTime as Date | undefined;
      const endTime = data.EndTime as Date | undefined;
      if (!startTime || !endTime) return;

      const itemId = data.Id as string | undefined;
      if (!itemId) return;
      const storeItem = useScheduleStore.getState().items.find((i) => i.id === itemId);
      if (!storeItem) return;

      const origStart = new Date(storeItem.startDate);
      origStart.setHours(0, 0, 0, 0);

      const isRightEdge =
        startTime.getFullYear() === origStart.getFullYear() &&
        startTime.getMonth() === origStart.getMonth() &&
        startTime.getDate() === origStart.getDate();

      if (!isRightEdge) return;

      const corrected = new Date(endTime);
      corrected.setDate(corrected.getDate() - 1);
      corrected.setHours(23, 59, 59, 0);

      if (corrected >= startTime) {
        data.EndTime = corrected;
      }
    },
    [],
  );

  /** Handle completed drag/resize/edit actions — write back to monday.com */
  const onActionComplete = useCallback(
    (args: ActionEventArgs) => {
      if (!args.data) return;

      if (args.requestType === 'eventChanged') {
        // After a drag/resize, Syncfusion already updated the event position
        // visually. Our store update will trigger a new `events` array which
        // causes Syncfusion to do a full dataSource re-render — blinking all
        // events. Skip the next events recomputation so Syncfusion keeps its
        // already-correct internal state.
        skipNextEventsUpdateRef.current = true;

        const changedEvents = Array.isArray(args.data) ? args.data : [args.data];

        for (const evt of changedEvents) {
          // Handle resized leave events — persist new dates via callback
          if (evt.IsLeave) {
            if (isResizableLeave(evt as Record<string, unknown>) && onLeaveResized) {
              const startTime = evt.StartTime as Date;
              const endTime = evt.EndTime as Date;
              // Strip team suffix from Id (e.g. "uuid_t123" → "uuid")
              const rawId = String(evt.Id);
              const leaveId = rawId.includes('_t') ? rawId.split('_t')[0] : rawId;
              onLeaveResized(leaveId, format(startTime, 'yyyy-MM-dd'), format(endTime, 'yyyy-MM-dd'));
            }
            continue;
          }
          const mondayItemId = evt.MondayItemId as number;
          if (!mondayItemId) continue;

          // Resolve the correct board and field mapping for this event.
          // Subitems live on a separate board and use their own field mapping.
          const evtBoardId = (evt.BoardId as number | undefined) ?? boardId;
          const isSubitem = evt.IsSubitem as boolean;
          const subitemsBoardId = evt.SubitemsBoardId as number | undefined;
          const effectiveBoardId = (isSubitem && subitemsBoardId) ? subitemsBoardId : evtBoardId;
          const fieldMapping = isSubitem
            ? getSubitemFieldMappingLocal(evtBoardId)
            : getFieldMappingLocal(evtBoardId);
          if (!fieldMapping) continue;

          const startTime = evt.StartTime as Date;
          const endTime = evt.EndTime as Date;

          const newStart = format(startTime, 'yyyy-MM-dd');
          const newEnd = format(endTime, 'yyyy-MM-dd');

          // Detect resource fields depending on mode:
          // - teams-only: Syncfusion uses `TeamRowId` field (team id)
          // - teamWithUsers: two-level `TeamId` + `AssigneeId`
          // - assignees: flat `AssigneeId` (user id)
          const newTeamRowId = evt.TeamRowId as number | undefined;   // teams-only mode
          const newTeamId = evt.TeamId as number | undefined;         // teamWithUsers mode
          const newAssigneeId = evt.AssigneeId as number | undefined; // user modes

          const itemId = evt.Id as string;

          // Look up the store item BEFORE updating to get old assignee info
          const storeItem = useScheduleStore.getState().items.find((i) => i.id === itemId);
          const oldAssigneeId = storeItem?.assigneeId ?? 0;
          const oldTeamId = storeItem?.assignedTeamId;

          // Alt (Option on Mac) held = add assignee mode (split),
          // only when the resource row actually changed
          const resourceChanged =
            (isTeamsOnly && newTeamRowId !== undefined && newTeamRowId > 0 && newTeamRowId !== oldTeamId) ||
            (!isTeamsOnly && (newAssigneeId ?? 0) !== oldAssigneeId);
          const addOnly = modifierKeyRef.current && resourceChanged;

          if (isTeamsOnly && newTeamRowId !== undefined && newTeamRowId > 0) {
            // ── Teams-only mode: item was moved between team rows ──
            if (addOnly && storeItem) {
              // Revert the dragged item back to its original row
              useScheduleStore.getState().updateItem(itemId, {
                startDate: newStart,
                endDate: newEnd,
              });
              // Optimistically add a clone for the new team
              useScheduleStore.getState().addItem({
                ...storeItem,
                id: `${storeItem.id}-split-${newTeamRowId}`,
                assigneeId: 0,
                assignedTeamId: newTeamRowId,
                startDate: newStart,
                endDate: newEnd,
              });
            } else {
              // Check if the target team already has a sibling for this
              // mondayItemId. If so, just remove the dragged row to avoid
              // duplicates (the item already appears on the target team).
              const store = useScheduleStore.getState();
              const targetTeamExists = store.items.some(
                (i) => i.mondayItemId === mondayItemId && i.id !== itemId &&
                  i.assignedTeamId === newTeamRowId,
              );
              if (targetTeamExists) {
                store.removeItem(itemId);
                // Tell Syncfusion to drop the event it visually moved so it
                // doesn't linger as a duplicate on the target team row.
                scheduleRef.current?.deleteEvent(itemId, 'DeleteSeries');
                skipNextEventsUpdateRef.current = false;
              } else {
                // Only update the timeline. Write team assignment to monday.com.
                store.updateItem(itemId, {
                  startDate: newStart,
                  endDate: newEnd,
                });
              }
            }

            if (fieldMapping.timelineColumnId) {
              updateItemTimeline(effectiveBoardId, mondayItemId, fieldMapping.timelineColumnId, newStart, newEnd).catch(console.error);
            }
            if (fieldMapping.assigneesColumnId) {
              const entries = buildReassignedEntries(
                useScheduleStore.getState().items, mondayItemId,
                oldAssigneeId, oldTeamId,
                0, newTeamRowId, 'team',
                addOnly,
              );
              updateItemAssignee(effectiveBoardId, mondayItemId, fieldMapping.assigneesColumnId, entries).catch(console.error);
            }
          } else {
            // ── Assignees or teamWithUsers mode ──
            const resolvedAssigneeId = newAssigneeId ?? 0;

            if (addOnly && storeItem) {
              // Revert the dragged item back to its original assignee row
              useScheduleStore.getState().updateItem(itemId, {
                startDate: newStart,
                endDate: newEnd,
              });
              // Optimistically add a clone for the new assignee
              const cloneId = `${itemId}-split-${resolvedAssigneeId || newTeamId}`;
              useScheduleStore.getState().addItem({
                ...storeItem,
                id: cloneId,
                assigneeId: resolvedAssigneeId,
                assignedTeamId: (isTeamWithUsers && newTeamId) ? newTeamId : storeItem.assignedTeamId,
                startDate: newStart,
                endDate: newEnd,
              });
            } else {
              // Check if the target assignee already has a sibling for this
              // mondayItemId. If so, removing the dragged item's old assignee
              // is enough — updating its assigneeId would create a visible
              // duplicate until the next refresh.
              const store = useScheduleStore.getState();
              const targetAlreadyExists = store.items.some(
                (i) => i.mondayItemId === mondayItemId && i.id !== itemId &&
                  i.assigneeId === resolvedAssigneeId,
              );
              if (targetAlreadyExists && resolvedAssigneeId > 0) {
                // The target resource already has this item — just remove the
                // dragged row (old assignee is being dropped).
                store.removeItem(itemId);
                // Tell Syncfusion to drop the event it visually moved so it
                // doesn't linger as a duplicate on the target resource row.
                scheduleRef.current?.deleteEvent(itemId, 'DeleteSeries');
                // Allow the events useMemo to recompute so the removed item
                // disappears immediately.
                skipNextEventsUpdateRef.current = false;
              } else {
                const storeUpdate: Partial<ScheduleItem> = {
                  startDate: newStart,
                  endDate: newEnd,
                  assigneeId: resolvedAssigneeId,
                };
                // In teamWithUsers mode, persist the team so the event stays in the
                // correct team row (users can belong to multiple teams).
                if (isTeamWithUsers && newTeamId !== undefined && newTeamId > 0) {
                  storeUpdate.assignedTeamId = newTeamId;
                }
                store.updateItem(itemId, storeUpdate);
              }
            }

            if (fieldMapping.timelineColumnId) {
              updateItemTimeline(effectiveBoardId, mondayItemId, fieldMapping.timelineColumnId, newStart, newEnd).catch(console.error);
            }

            if (fieldMapping.assigneesColumnId) {
              if (resolvedAssigneeId > 0) {
                // Build full assignee list preserving other assignees
                const entries = buildReassignedEntries(
                  useScheduleStore.getState().items, mondayItemId,
                  oldAssigneeId, oldTeamId,
                  resolvedAssigneeId, undefined, 'person',
                  addOnly,
                );
                updateItemAssignee(effectiveBoardId, mondayItemId, fieldMapping.assigneesColumnId, entries).catch(console.error);
              } else if (newTeamId !== undefined && newTeamId > 0) {
                // Dropped on a team header in teamWithUsers two-level mode
                const entries = buildReassignedEntries(
                  useScheduleStore.getState().items, mondayItemId,
                  oldAssigneeId, oldTeamId,
                  0, newTeamId, 'team',
                  addOnly,
                );
                updateItemAssignee(effectiveBoardId, mondayItemId, fieldMapping.assigneesColumnId, entries).catch(console.error);
              }
            }
          }

          // After split or reassign, redistribute hours across all siblings
          // so event cards show the correct per-person share
          if (addOnly || resourceChanged) {
            const store = useScheduleStore.getState();
            const perPersonDist = getPerPersonDistributionLocal(evtBoardId) ??
              useWorkloadStore.getState().perPersonDistribution;
            redistributeHours(store.updateItem, store.items, mondayItemId, perPersonDist);
          }

          // When a Ctrl+drag clone was added, or when siblings need timeline
          // sync, the store has new/changed items that must appear in the
          // Syncfusion dataSource. Reset the skip flag so the `events` useMemo
          // recomputes instead of returning stale data.
          if (addOnly) {
            skipNextEventsUpdateRef.current = false;
          }

          // Sync timeline across all sibling items (other assignees of the same
          // monday.com item) so multi-assignee items always share the same dates.
          {
            const store = useScheduleStore.getState();
            const hasSiblings = store.items.some(
              (i) => i.mondayItemId === mondayItemId && i.id !== itemId &&
                (i.startDate !== newStart || i.endDate !== newEnd),
            );
            if (hasSiblings) {
              syncSiblingTimelines(store.updateItem, store.items, mondayItemId, newStart, newEnd, itemId);
              skipNextEventsUpdateRef.current = false;
            }
          }
        }
      }
    },
    [boardId, isTeamsOnly, isTeamWithUsers, isResizableLeave, onLeaveResized],
  );

  /** Handle event click — show tooltip or leave detail dialog */
  const onEventClick = useCallback(
    (args: EventClickArgs) => {
      // Prevent Syncfusion's default popup
      args.cancel = true;
      const eventData = args.event as Record<string, unknown>;
      const eventId = eventData.Id as string;

      // Holiday events — no action on click
      if (eventData.IsHoliday) return;

      if (eventData.IsWorklog) {
        // Worklog event clicked → show worklog tooltip at click position
        if (eventId && onWorklogClick) {
          const worklogId = (eventData.WorklogId as string) || eventId.replace(/^wl_/, '').replace(/_t\d+$/, '');
          const el = args.element as HTMLElement;
          const rect = el.getBoundingClientRect();
          onWorklogClick(worklogId, {
            x: rect.left + rect.width / 2,
            y: rect.bottom + 4,
          });
        }
        return;
      }

      if (eventData.IsLeave) {
        // Leave event clicked → show leave tooltip at click position
        if (eventId && onLeaveClick) {
          // Strip the team suffix (e.g. "leave_123_t456" → "leave_123")
          const realId = eventId.replace(/_t\d+$/, '');
          const el = args.element as HTMLElement;
          const rect = el.getBoundingClientRect();
          onLeaveClick(realId, {
            x: rect.left + rect.width / 2,
            y: rect.bottom + 4,
          });
        }
      } else {
        // Regular task clicked → show info tooltip at click position
        if (eventId && onTaskClick) {
          // Strip the team suffix for duplicated events
          const realId = eventId.replace(/_t\d+$/, '');
          const el = args.element as HTMLElement;
          const rect = el.getBoundingClientRect();
          onTaskClick(realId, {
            x: rect.left + rect.width / 2,
            y: rect.bottom + 4,
          });
        }
      }
    },
    [onTaskClick, onLeaveClick, onWorklogClick],
  );

  /** Track latest cell range selection (multi-cell drag). Updated on every cell
   *  selection change; the context menu is opened on mouseup from the native
   *  click handler below. Single-cell clicks go through the click listener. */
  const pendingCellSelectionRef = useRef<{
    startTime: Date;
    endTime: Date;
    groupIndex: number;
    teamId?: number;
    assigneeId?: number;
  } | null>(null);

  const onSelect = useCallback(
    (args: Record<string, unknown>) => {
      // Skip during event drags / resizes
      if (isDraggingEventRef.current) return;
      // Only handle cell selections (not event selections)
      if (args.requestType !== 'cellSelect' && args.requestType !== 'cellChange') return;

      const data = args.data as Record<string, unknown> | undefined;
      if (!data) return;

      const startTime = data.startTime ?? data.StartTime;
      const endTime = data.endTime ?? data.EndTime;
      if (!(startTime instanceof Date) || !(endTime instanceof Date)) return;

      // Only track multi-cell selections (single click resolved via native click)
      const diffMs = endTime.getTime() - startTime.getTime();
      if (diffMs <= 24 * 60 * 60 * 1000) {
        pendingCellSelectionRef.current = null;
        return;
      }

      pendingCellSelectionRef.current = {
        startTime,
        endTime,
        groupIndex: typeof data.groupIndex === 'number' ? data.groupIndex : -1,
        teamId: typeof data.TeamId === 'number' ? data.TeamId : undefined,
        assigneeId: typeof data.AssigneeId === 'number' ? data.AssigneeId : undefined,
      };
    },
    [],
  );

  // Keep a stable ref to onCellClick so the native click listener (installed once)
  // always calls the latest callback without re-binding.
  const onCellClickRef = useRef(onCellClick);
  onCellClickRef.current = onCellClick;

  // Unified native mouseup listener: opens the cell context menu for both
  // single-clicks on an empty cell AND for multi-cell drag selections. `mouseup`
  // is used instead of `click` because some browsers suppress the `click` event
  // after a drag selection, which would silently break drag-to-create.
  // Clicks on event bars (.e-appointment / .e-appointment-wrapper) are ignored —
  // those are handled by onTaskClick / onLeaveClick.
  useEffect(() => {
    const schedulerEl = scheduleRef.current?.element;
    if (!schedulerEl) return;

    const onMouseUp = (e: MouseEvent) => {
      if (!onCellClickRef.current) return;
      // Only left button
      if (e.button !== 0) return;
      // Skip during event drags / resizes
      if (isDraggingEventRef.current) return;

      const target = e.target as HTMLElement;
      if (!target) return;

      // Ignore clicks on event bars — those route to onTaskClick / onLeaveClick.
      // Also ignore the per-day workload overlay — it opens the capacity popup on
      // click, and the create menu must not fire underneath it (the overlay's own
      // stopPropagation is on the `click` event, which is too late for this
      // `mouseup` listener).
      if (target.closest('.e-appointment, .e-appointment-wrapper, .rp-sf-workload-cell')) return;

      // Only handle clicks inside a work cell (empty scheduler cell)
      const cell = target.closest('.e-work-cells') as HTMLElement | null;
      if (!cell) return;

      const schedule = scheduleRef.current;
      if (!schedule) return;

      // If a multi-cell selection is pending (user drag-selected across cells),
      // use its range; otherwise resolve the single cell under the click.
      const pending = pendingCellSelectionRef.current;
      pendingCellSelectionRef.current = null;

      let startTime: Date;
      let endTime: Date | undefined;
      let resolvedAssigneeId = 0;
      let resolvedKind: 'person' | 'team' = 'person';

      if (pending) {
        startTime = pending.startTime;
        endTime = pending.endTime;

        if (pending.assigneeId && pending.assigneeId > 0) {
          resolvedAssigneeId = pending.assigneeId;
        } else if (isTeamsOnlyRef.current && pending.groupIndex >= 0) {
          resolvedAssigneeId = resourcesRef.current[pending.groupIndex]?.id ?? 0;
          resolvedKind = 'team';
        } else if (
          isTeamWithUsersRef.current &&
          pending.teamId &&
          pending.teamId > 0 &&
          !pending.assigneeId
        ) {
          resolvedAssigneeId = pending.teamId;
          resolvedKind = 'team';
        } else if (pending.groupIndex >= 0) {
          resolvedAssigneeId = resourcesRef.current[pending.groupIndex]?.id ?? 0;
        }
      } else {
        // Single-cell click — resolve date and row from the DOM cell
        const cellData = schedule.getCellDetails(cell);
        if (!cellData) return;
        const cellStart = cellData.startTime;
        const cellEnd = cellData.endTime;
        if (!(cellStart instanceof Date)) return;
        startTime = cellStart;
        endTime = cellEnd instanceof Date ? cellEnd : undefined;

        // Parent group row (team in teamWithUsers mode) — Syncfusion marks these
        // with .e-resource-group-cells; resolve via getResourcesByIndex.
        const isParentGroupCell = cell.classList.contains('e-resource-group-cells');
        const attr = cell.getAttribute('data-group-index');

        if (isParentGroupCell && attr != null) {
          const groupIdx = parseInt(attr, 10);
          try {
            const resourceDetails = schedule.getResourcesByIndex(groupIdx);
            if (resourceDetails?.resourceData) {
              const rd = resourceDetails.resourceData as Record<string, unknown>;
              resolvedAssigneeId = (rd.id as number) ?? 0;
            }
          } catch {
            // fall through
          }
          resolvedKind = 'team';
        } else if (attr != null) {
          const groupIdx = parseInt(attr, 10);
          try {
            const resourceDetails = schedule.getResourcesByIndex(groupIdx);
            if (resourceDetails?.resourceData) {
              const rd = resourceDetails.resourceData as Record<string, unknown>;
              resolvedAssigneeId = (rd.id as number) ?? 0;
            }
          } catch {
            resolvedAssigneeId = resourcesRef.current[groupIdx]?.id ?? 0;
          }
          if (isTeamsOnlyRef.current) resolvedKind = 'team';
        }
      }

      // Syncfusion's endTime is exclusive — subtract 1 day to get inclusive end
      let adjustedEnd: Date | undefined;
      if (endTime instanceof Date) {
        const adjusted = new Date(endTime);
        adjusted.setDate(adjusted.getDate() - 1);
        adjustedEnd = adjusted >= startTime ? adjusted : startTime;
      }

      onCellClickRef.current(
        startTime,
        resolvedAssigneeId,
        adjustedEnd,
        resolvedKind,
        e.clientX,
        e.clientY,
      );
    };

    schedulerEl.addEventListener('mouseup', onMouseUp);
    return () => schedulerEl.removeEventListener('mouseup', onMouseUp);
  }, []);

  /** Block ALL Syncfusion default popups — our context menu replaces them. */
  const onPopupOpen = useCallback(
    (args: PopupOpenEventArgs) => {
      // Always cancel Syncfusion's built-in popups (Editor / QuickInfo / etc.).
      args.cancel = true;
    },
    [],
  );

  /** Handle view navigation — sync date range to store */
  const onNavigating = useCallback(
    (args: NavigatingEventArgs) => {
      if (onDateRangeChange && args.currentDate) {
        const newDate = args.currentDate as Date;
        const start = format(newDate, 'yyyy-MM-dd');
        // End date will be computed by the store based on view mode
        onDateRangeChange(start, start);
      }
    },
    [onDateRangeChange],
  );

  /**
   * After Syncfusion renders, sync content-table row heights with resource-column
   * row heights. They live in separate <table> elements so their row heights can diverge
   * when rowAutoHeight expands one side (e.g., many stacked events on content side).
   *
   * CSS `.rp-syncfusion-scheduler--workload` sets a matching `height: 75px` on both
   * tables' <tr> elements, which handles the workload indicator height mismatch.
   * This function handles edge cases where rowAutoHeight pushes one side taller.
   *
   * We intentionally do NOT call refreshEvents() — it causes a visible "jump".
   */
  const syncRowHeights = useCallback(() => {
    const el = scheduleRef.current?.element;
    if (!el) return;

    const resourceRows = el.querySelectorAll<HTMLElement>(
      '.e-resource-column-table tbody tr',
    );
    const contentRows = el.querySelectorAll<HTMLElement>(
      '.e-content-table tbody tr',
    );

    if (!resourceRows.length || !contentRows.length) return;

    const count = Math.min(resourceRows.length, contentRows.length);
    for (let i = 0; i < count; i++) {
      const resourceHeight = resourceRows[i].getBoundingClientRect().height;
      const contentHeight = contentRows[i].getBoundingClientRect().height;

      if (resourceHeight > contentHeight) {
        contentRows[i].style.height = `${resourceHeight}px`;
      } else if (contentHeight > resourceHeight) {
        resourceRows[i].style.height = `${contentHeight}px`;
      }
    }
  }, []);

  /** Inject the per-day workload indicator into each timeline cell.
   *  Imperative DOM (not a Syncfusion template) so mode / unit / data changes
   *  repaint instantly without a full grid refresh. One pass clears old overlays
   *  and rebuilds them from the current workload entries. Reads volatile state
   *  from refs so the callback stays stable. */
  const refreshWorkloadCells = useCallback(() => {
    const schedulerEl = scheduleRef.current?.element;
    if (!schedulerEl) return;

    // Full repaint: drop any previous overlays first.
    schedulerEl.querySelectorAll('.rp-sf-workload-cell').forEach((n) => n.remove());
    if (!workloadEnabledRef.current) return;

    const entries = workloadEntriesRef.current;
    const issues = issueCountEntriesRef.current;
    if (!entries.length && !issues.length) return;

    const indicatorType = indicatorTypeRef.current;
    const displayMode = workloadDisplayModeRef.current;
    const isYearMonthly = viewModeRef.current === 'year-monthly';
    const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Per-(user, day) lookups.
    const dayMap = new Map<string, WorkloadEntry>();
    for (const e of entries) dayMap.set(`${e.userId}_${e.date}`, e);
    const issueMap = new Map<string, number>();
    for (const i of issues) issueMap.set(`${i.userId}_${i.date}`, i.issueCount);

    // Year (monthly) view: pre-aggregate each user's days into months. The visible
    // range is a single calendar year, so the month name uniquely keys the bucket.
    const monthMap = new Map<string, AggregatedEntry>();
    const monthIssueMap = new Map<string, number>();
    if (isYearMonthly) {
      const byUser = new Map<number, WorkloadEntry[]>();
      for (const e of entries) {
        const arr = byUser.get(e.userId);
        if (arr) arr.push(e);
        else byUser.set(e.userId, [e]);
      }
      for (const [uid, list] of byUser) {
        for (const agg of aggregateByMonth(list)) monthMap.set(`${uid}_${agg.label}`, agg);
      }
      // Aggregate issue counts per month (yyyy-MM-dd → month index via string slice,
      // avoiding any date parsing) so "Number of items" mode has values in year view.
      for (const i of issues) {
        const monthIdx = parseInt(i.date.slice(5, 7), 10) - 1;
        const key = `${i.userId}_${MONTH_ABBR[monthIdx]}`;
        monthIssueMap.set(key, (monthIssueMap.get(key) ?? 0) + i.issueCount);
      }
    }

    // Cache resource resolution per group index (getResourcesByIndex is not free).
    const resourceCache = new Map<number, { id: number; name: string } | null>();
    const resolveResource = (groupIdx: number): { id: number; name: string } | null => {
      if (resourceCache.has(groupIdx)) return resourceCache.get(groupIdx) ?? null;
      let resolved: { id: number; name: string } | null = null;
      try {
        const res = scheduleRef.current?.getResourcesByIndex(groupIdx);
        const rd = res?.resourceData as Record<string, unknown> | undefined;
        if (rd && typeof rd.id === 'number' && rd.id > 0) {
          resolved = { id: rd.id, name: (rd.text as string) ?? '' };
        }
      } catch { /* leave null */ }
      resourceCache.set(groupIdx, resolved);
      return resolved;
    };

    const cells = schedulerEl.querySelectorAll<HTMLElement>('td.e-work-cells, td.e-month-cells');
    cells.forEach((cell) => {
      const groupAttr = cell.getAttribute('data-group-index');
      const dateAttr = cell.getAttribute('data-date');
      if (groupAttr == null || !dateAttr) return;

      const resource = resolveResource(parseInt(groupAttr, 10));
      if (!resource) return;

      const cellDate = new Date(parseInt(dateAttr, 10));
      let input: WorkloadCellInput;
      let issueCount = 0;
      let popupEntries: AggregatedEntry[];

      if (isYearMonthly) {
        const monthAbbr = MONTH_ABBR[cellDate.getMonth()];
        const agg = monthMap.get(`${resource.id}_${monthAbbr}`);
        if (!agg) return;
        input = agg;
        issueCount = monthIssueMap.get(`${resource.id}_${monthAbbr}`) ?? 0;
        popupEntries = [agg];
      } else {
        const dateStr = format(cellDate, 'yyyy-MM-dd');
        const dayEntry = dayMap.get(`${resource.id}_${dateStr}`);
        if (!dayEntry) return;
        input = dayEntry;
        issueCount = issueMap.get(`${resource.id}_${dateStr}`) ?? 0;
        popupEntries = aggregateByDay([dayEntry]);
      }

      const d = getWorkloadCellDisplay(input, issueCount, indicatorType, displayMode);
      if (!d.render) return;

      const container = document.createElement('div');
      container.className = 'rp-sf-workload-cell' + (isYearMonthly ? ' rp-sf-workload-cell--monthly' : '');
      if (d.title) container.title = d.title;

      if (d.showTrack) {
        const track = document.createElement('div');
        track.className = 'rp-sf-workload-cell__track';
        const fill = document.createElement('div');
        fill.className = 'rp-sf-workload-cell__fill' + (d.striped ? ' rp-sf-workload-cell__fill--striped' : '');
        fill.style.width = `${d.fillPct}%`;
        fill.style.backgroundColor = d.fillColor;
        if (d.fillOpacity != null) fill.style.opacity = String(d.fillOpacity);
        track.appendChild(fill);
        if (d.overflowPct > 0) {
          const overflow = document.createElement('div');
          overflow.className = 'rp-sf-workload-cell__overflow';
          overflow.style.width = `${d.overflowPct}%`;
          overflow.style.backgroundColor = d.overflowColor;
          track.appendChild(overflow);
        }
        container.appendChild(track);
      }

      const label = document.createElement('span');
      label.className = 'rp-sf-workload-cell__value';
      label.style.color = d.labelColor;
      label.textContent = d.label;
      container.appendChild(label);

      // Click → capacity detail popup anchored below this cell.
      container.addEventListener('click', (evt) => {
        evt.stopPropagation();
        const rect = container.getBoundingClientRect();
        showCapacityPopup({
          userName: resource.name,
          aggregatedEntries: popupEntries,
          anchorRect: { top: rect.bottom + 4, left: rect.left },
        });
      });

      cell.style.position = 'relative';
      cell.appendChild(container);
    });
  }, [showCapacityPopup]);

  /** Called after Syncfusion finishes rendering data. */
  const onDataBound = useCallback(() => {
    requestAnimationFrame(() => {
      syncRowHeights();
      refreshWorkloadCells();
    });
  }, [syncRowHeights, refreshWorkloadCells]);

  // When workload toggle changes, row heights change via CSS (.rp-syncfusion-scheduler--workload).
  // Syncfusion doesn't know about this, so events stay positioned at old offsets.
  // Re-layout after the CSS class has been applied.
  useEffect(() => {
    requestAnimationFrame(() => {
      syncRowHeights();
      scheduleRef.current?.refreshEvents();
    });
  }, [workloadEnabled, syncRowHeights]);

  // Repaint the per-cell workload overlays whenever the workload data, active
  // mode, unit, or view changes — Syncfusion does not re-run cell rendering for
  // these, so the imperative overlay must be rebuilt explicitly.
  useEffect(() => {
    const id = requestAnimationFrame(() => refreshWorkloadCells());
    return () => cancelAnimationFrame(id);
  }, [workloadEnabled, indicatorType, workloadDisplayMode, workloadEntries, issueCountEntries, viewMode, refreshWorkloadCells]);

  // The capacity popup opens on a cell-overlay click, so dismiss it on Escape or a
  // click/scroll outside the popup (clicks on another overlay open a new one).
  useEffect(() => {
    if (!capacityPopup) return;
    const closeIfOutside = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('.rp-capacity-popup') || t.closest('.rp-sf-workload-cell'))) return;
      setCapacityPopup(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCapacityPopup(null);
    };
    document.addEventListener('mousedown', closeIfOutside, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', closeIfOutside, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [capacityPopup]);

  /** Highlight holiday cells + workload heatmap.
   *  Uses refs for volatile data so this callback reference stays stable
   *  and doesn't cause Syncfusion to re-render all events when workload changes. */
  const onRenderCell = useCallback(
    (args: RenderCellEventArgs) => {
      if (args.elementType === 'workCells' || args.elementType === 'monthCells') {
        const cellDate = args.date;
        if (!cellDate) return;

        const el = args.element as HTMLElement;
        const dateStr = format(cellDate, 'yyyy-MM-dd');

        // Resolve per-user capacity schedule from the cell's resource row.
        // Syncfusion timeline cells have a data-group-index attribute that maps
        // to the resource at that row index.
        const isDark = document.body.classList.contains('dark-app-theme');
        const defaultSched = defaultScheduleRef.current;
        const dowKey = DOW_TO_KEY[getDay(cellDate)];

        let userSched: Record<string, number> | DaySchedule | null = defaultSched;
        const groupAttr = el.getAttribute('data-group-index');
        if (groupAttr != null && scheduleRef.current) {
          const groupIdx = parseInt(groupAttr, 10);
          try {
            const resourceDetails = scheduleRef.current.getResourcesByIndex(groupIdx);
            if (resourceDetails?.resourceData) {
              const userId = (resourceDetails.resourceData as Record<string, unknown>).id as number;
              if (userId) {
                const perUser = userSchedulesRef.current.get(userId);
                if (perUser) userSched = perUser;
              }
            }
          } catch {
            // Fallback to default schedule if resource lookup fails
          }
        }

        const isNonWorking = userSched ? (userSched as Record<string, number>)[dowKey] <= 0 : false;
        if (userSched) {
          if (isNonWorking) {
            el.style.setProperty(
              'background-color',
              isDark ? 'rgba(255, 255, 255, 0.03)' : '#f0f0f0',
              'important',
            );
          } else {
            el.style.backgroundColor = isDark ? '#30324e' : '#ffffff';
          }
        }

        // Holidays → light orange/dark amber background (overrides non-working day)
        const holidayName = holidaysRef.current?.get(dateStr);
        if (holidayName) {
          el.style.setProperty(
            'background-color',
            isDark ? 'rgba(255, 152, 0, 0.08)' : '#fff3e0',
            'important',
          );
          el.title = holidayName;
        }
      }
    },
    // Stable: reads from refs at call time
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** Apply custom color to events; leave events colored by approval status */
  const onEventRendered = useCallback(
    (args: EventRenderedArgs) => {
      const data = args.data as Record<string, unknown>;
      if (!args.element) return;

      const isDarkEvt = document.body.classList.contains('dark-app-theme');

      if (data.IsHoliday) {
        // Holiday events: orange-tinted background, non-interactive
        args.element.style.backgroundColor = isDarkEvt ? 'rgba(255, 152, 0, 0.18)' : '#fff3e0';
        args.element.style.borderColor = '#ff9800';
        args.element.style.border = '1px solid #ff9800';
        args.element.style.cursor = 'default';
        args.element.classList.add('rp-sf-holiday-event');
      } else if (data.IsWorklog) {
        // Worklog events: subtle gray with dark left accent border
        args.element.style.backgroundColor = isDarkEvt ? 'rgba(197, 199, 208, 0.15)' : '#f5f6f8';
        args.element.style.border = '1px solid ' + (isDarkEvt ? '#4a4c5e' : '#d0d4e4');
        args.element.style.borderLeft = '3px solid ' + (isDarkEvt ? '#9699a6' : '#676879');
        args.element.style.color = isDarkEvt ? '#d5d8df' : '#323338';
        args.element.classList.add('rp-sf-worklog-event');
      } else if (data.IsLeave) {
        // Color by leave status — light / dark pairs
        const status = data.LeaveStatus as string;
        let bgColor: string;
        let borderColor: string;
        if (status === 'Approved') {
          bgColor = isDarkEvt ? 'rgba(0, 200, 117, 0.15)' : '#d4f5e9';
          borderColor = '#00c875';
        } else if (status === 'Rejected') {
          bgColor = isDarkEvt ? 'rgba(216, 58, 82, 0.15)' : '#fde8ec';
          borderColor = isDarkEvt ? '#e2445c' : '#d83a52';
        } else if (status === 'Recalled') {
          bgColor = isDarkEvt ? 'rgba(150, 153, 166, 0.15)' : '#e8e8e8';
          borderColor = isDarkEvt ? '#9699a6' : '#808080';
        } else {
          // Pending
          bgColor = isDarkEvt ? 'rgba(253, 171, 61, 0.15)' : '#fff3e0';
          borderColor = '#fdab3d';
        }
        args.element.style.backgroundColor = bgColor;
        args.element.style.borderColor = borderColor;
        args.element.style.border = `1px solid ${borderColor}`;
        const stripeColor = isDarkEvt ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
        args.element.style.backgroundImage =
          `repeating-linear-gradient(135deg, transparent, transparent 4px, ${stripeColor} 4px, ${stripeColor} 8px)`;
        args.element.classList.add('rp-sf-leave-event');
      } else {
        const color = data.Color as string;
        if (color) {
          args.element.style.backgroundColor = color;
          args.element.style.borderColor = color;
        }

        // Overlog indicator: red dashed border on the Syncfusion element itself
        const curInd = useWorkloadStore.getState().indicatorType;
        const isRemMode = curInd === 'remaining_estimate' || curInd === 'worklog_workload' || curInd === 'availability';
        if (isRemMode) {
          const hours = data.Hours as number;
          const format = data.WorkHourFormat as string;
          if (hours > 0 && format === 'total') {
            const mondayItemId = String(data.MondayItemId ?? '');
            const logged = worklogsByItemRef.current.get(mondayItemId) ?? 0;
            const origTotal = (data.OriginalTotalHours as number) ?? hours;
            if (isItemOverlogged(origTotal, logged)) {
              args.element.style.border = `2px dashed #e2445c`;
              args.element.style.borderLeft = `4px solid #e2445c`;
            }
          }
        }
      }
    },
    [],
  );

  /** Build workload summary for a specific user across the visible date range.
   *  Uses refs so the callback stays stable across workload data updates. */
  const buildWorkloadSummary = useCallback(
    (userId: number): { totalScheduled: number; totalCapacity: number; entries: WorkloadEntry[]; aggregated: AggregatedEntry[] } => {
      const entries = workloadEntriesRef.current;
      if (!entries.length) return { totalScheduled: 0, totalCapacity: 0, entries: [], aggregated: [] };

      const userEntries = entries.filter((e) => e.userId === userId);
      const totalScheduled = userEntries.reduce((sum, e) => sum + e.scheduledHours, 0);
      const totalCapacity = userEntries.reduce((sum, e) => sum + e.capacityHours, 0);

      // Aggregate based on view mode
      const vm = viewModeRef.current;
      const isYearView = vm === 'year-monthly' || vm === 'year-daily';
      const aggregated = isYearView
        ? aggregateByMonth(userEntries)
        : aggregateByDay(userEntries);

      return { totalScheduled, totalCapacity, entries: userEntries, aggregated };
    },
    // Stable: reads from refs at call time
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** Custom resource header showing avatar + name.
   *  The workload indicator is no longer here — it is drawn per day cell by
   *  refreshWorkloadCells. This callback stays stable so workload data changes
   *  don't cause Syncfusion to re-render events. */
  const resourceHeaderTemplate = useCallback(
    (props: Record<string, unknown>) => {
      const resourceData = props.resourceData as Record<string, unknown>;
      const name = (resourceData?.text as string) || 'Unknown';
      const avatar = resourceData?.avatar as string | null;

      // Detect if this is a team-level header (no avatar field = team resource)
      const isTeamHeader = isTeamMode && !('avatar' in (resourceData ?? {}));
      if (isTeamHeader) {
        return (
          <div className="rp-sf-resource-header rp-sf-resource-header--team">
            <div className="rp-sf-resource-header__info">
              <div className="rp-sf-resource-header__team-icon">T</div>
              <span className="rp-sf-resource-header__name rp-sf-resource-header__name--team">{name}</span>
            </div>
          </div>
        );
      }

      return (
        <div className="rp-sf-resource-header">
          <div className="rp-sf-resource-header__info">
            {avatar ? (
              <img
                className="rp-sf-resource-header__avatar"
                src={avatar}
                alt={name}
              />
            ) : (
              <div className="rp-sf-resource-header__avatar-placeholder">
                {name.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="rp-sf-resource-header__name">{name}</span>
          </div>
        </div>
      );
    },
    // Stable: the workload indicator now lives in the day cells, injected by
    // refreshWorkloadCells — not in this header template.
    [isTeamMode],
  );

  /** Custom event template showing task name + status + hours, leave label, or holiday name. */
  const eventTemplate = useCallback(
    (props: Record<string, unknown>) => {
      const subject = (props.Subject as string) || '';
      const isHoliday = props.IsHoliday as boolean;
      const isLeave = props.IsLeave as boolean;

      if (isHoliday) {
        return (
          <div className="rp-sf-event rp-sf-event--holiday">
            <span className="rp-sf-event__name rp-sf-event__name--holiday">
              {subject}
            </span>
          </div>
        );
      }

      if (props.IsWorklog) {
        const wlHours = props.Hours as number;
        const wlDesc = props.Description as string | null;
        return (
          <div className="rp-sf-event rp-sf-event--worklog">
            <div className="rp-sf-event__title-row">
              <span className="rp-sf-event__hours">{formatHoursMinutes(wlHours)}</span>
              <span className="rp-sf-event__name">{subject}</span>
            </div>
            {wlDesc && (
              <div className="rp-sf-event__meta">
                <span className="rp-sf-event__worklog-desc">{wlDesc}</span>
              </div>
            )}
          </div>
        );
      }

      if (isLeave) {
        const leaveType = (props.LeaveType as string) || 'Leave';
        const dayType = (props.DayType as string) || 'full';
        const dayLabel = dayType === 'full' ? '' : dayType === 'am' ? ' (AM)' : ' (PM)';
        const leaveStatus = (props.LeaveStatus as string) || 'Pending';
        return (
          <div className="rp-sf-event rp-sf-event--leave">
            <div className="rp-sf-event__meta">
              <span className="rp-sf-event__leave-status" data-status={leaveStatus.toLowerCase()}>
                {leaveStatus}
              </span>
            </div>
            <span className="rp-sf-event__name rp-sf-event__name--leave">
              {leaveType}{dayLabel}
            </span>
          </div>
        );
      }

      const hours = props.Hours as number;
      const status = props.Status as string | undefined;
      const statusColor = props.StatusColor as string | undefined;
      const itemFormat = props.WorkHourFormat as string | undefined;
      const hasConflict = props.HasHoursConflict as boolean;
      const isSubitem = props.IsSubitem as boolean;
      const parentItemName = props.ParentItemName as string | undefined;

      // In remaining estimate modes, show remaining hours on the task bar
      const curIndicator = indicatorType;
      const isRemainingMode = curIndicator === 'remaining_estimate' || curIndicator === 'worklog_workload' || curIndicator === 'availability';
      let displayHours = hours;
      let hoursSuffix = itemFormat === 'total' ? '' : '/d';
      let isOverlogged = false;
      if (isRemainingMode && hours > 0) {
        // Always show "rem" suffix in remaining modes for total items
        if (itemFormat === 'total') hoursSuffix = ' rem';

        const mondayItemId = String(props.MondayItemId ?? '');
        const itemWorklogs = worklogsByItemRef.current.get(mondayItemId) ?? 0;
        if (itemWorklogs > 0 && itemFormat !== 'per_day') {
          const originalTotal = (props.OriginalTotalHours as number) ?? hours;
          const perPersonDist = useWorkloadStore.getState().perPersonDistribution;
          const effectiveAssigneeCount = perPersonDist ? ((props.AssigneeCount as number) ?? 1) : 1;
          displayHours = computeRemainingPerPerson(originalTotal, itemWorklogs, effectiveAssigneeCount);
          isOverlogged = isItemOverlogged(originalTotal, itemWorklogs);
        }
      }

      return (
        <div className={`rp-sf-event${isSubitem ? ' rp-sf-event--subitem' : ''}${isOverlogged ? ' rp-sf-event--overlogged' : ''}`}>
          <div className="rp-sf-event__title-row">
            {hours > 0 && (
              <span className="rp-sf-event__hours" title={hasConflict ? 'Both hours columns have values. Using per-day value.' : isOverlogged ? `Overlogged: ${formatHoursMinutes(worklogsByItemRef.current.get(String(props.MondayItemId ?? '')) ?? 0)} logged vs ${formatHoursMinutes((props.OriginalTotalHours as number) ?? hours)} estimated` : undefined}>
                {hasConflict && <span className="rp-sf-event__conflict-icon">&#9888;</span>}
                {isOverlogged && <span className="rp-sf-event__overlog-icon" title="Worklogs exceed estimate">&#x26A0;</span>}
                {displayHours === 0 ? '0h' : formatHoursMinutes(displayHours)}{hoursSuffix}
              </span>
            )}
            {isSubitem && <span className="rp-sf-event__subitem-icon">&#8627;</span>}
            <span className="rp-sf-event__name">{subject}</span>
          </div>
          {(status || (isSubitem && parentItemName && getShowParentItemNameLocal(props.BoardId as number))) && (
            <div className="rp-sf-event__meta">
              {isSubitem && parentItemName && getShowParentItemNameLocal(props.BoardId as number) && (
                <span className="rp-sf-event__parent-badge" style={{ maxWidth: status ? 90 : 120 }}>{parentItemName}</span>
              )}
              {status && (
                <span
                  className="rp-sf-event__status"
                  style={statusColor ? { backgroundColor: statusColor } : undefined}
                >
                  {status}
                </span>
              )}
            </div>
          )}
        </div>
      );
    },
    // Re-create when indicator type, worklogs, or items change so task bars update
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indicatorType, worklogsByItem, items],
  );

  // Memoize eventSettings so Syncfusion only sees a new object when actual event data changes
  const eventSettings = useMemo(() => ({
    dataSource: events,
    template: eventTemplate as never,
    fields: {
      id: 'Id',
      subject: { name: 'Subject' },
      startTime: { name: 'StartTime' },
      endTime: { name: 'EndTime' },
      isAllDay: { name: 'IsAllDay' },
    },
  }), [events, eventTemplate]);

  // Determine the current resource layout mode key.
  // Syncfusion doesn't cleanly re-initialize when the resource structure changes,
  // so we force a full remount by changing the React `key`.
  // Include viewMode in the key so Syncfusion re-mounts when switching between
  // modes that share the same Syncfusion view (e.g. year-monthly / year-daily).
  const resourceModeKey = `${viewMode}_${isTeamsOnly
    ? 'teams-only'
    : isTeamWithUsers
      ? 'team-with-users'
      : 'assignees'}`;

  // A container/window resize makes Syncfusion re-layout and recreate the work
  // cells, discarding the injected overlays. Re-inject on resize. Re-observe when
  // the grid remounts (resourceModeKey change) so we track the new element.
  useEffect(() => {
    const el = scheduleRef.current?.element;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => refreshWorkloadCells());
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [refreshWorkloadCells, resourceModeKey]);

  // Build group config depending on mode
  const groupConfig = useMemo(() => {
    if (isTeamsOnly && teamsOnlyResources) {
      return { resources: ['TeamRows'] };
    }
    if (isTeamWithUsers && teamWithUsersResources) {
      return { resources: ['Teams', 'Assignees'] };
    }
    return { resources: ['Assignees'] };
  }, [isTeamsOnly, teamsOnlyResources, isTeamWithUsers, teamWithUsersResources]);

  // Build the ResourcesDirective children as an array (avoids Fragment issues with Syncfusion)
  const resourceDirectives = useMemo(() => {
    if (isTeamsOnly && teamsOnlyResources) {
      return (
        <ResourcesDirective>
          <ResourceDirective
            field="TeamRowId"
            title="Team"
            name="TeamRows"
            allowMultiple={false}
            dataSource={teamsOnlyResources}
            textField="text"
            idField="id"
            colorField="color"
          />
        </ResourcesDirective>
      );
    }
    if (isTeamWithUsers && teamWithUsersResources) {
      return (
        <ResourcesDirective>
          <ResourceDirective
            field="TeamId"
            title="Team"
            name="Teams"
            allowMultiple={false}
            dataSource={teamWithUsersResources.teams}
            textField="text"
            idField="id"
            colorField="color"
          />
          <ResourceDirective
            field="AssigneeId"
            title="Assignee"
            name="Assignees"
            allowMultiple={false}
            dataSource={teamWithUsersResources.assignees}
            textField="text"
            idField="id"
            groupIDField="teamId"
            colorField="color"
          />
        </ResourcesDirective>
      );
    }
    return (
      <ResourcesDirective>
        <ResourceDirective
          field="AssigneeId"
          title="Assignee"
          name="Assignees"
          allowMultiple={false}
          dataSource={assigneeResources}
          textField="text"
          idField="id"
          colorField="color"
        />
      </ResourcesDirective>
    );
  }, [isTeamsOnly, teamsOnlyResources, isTeamWithUsers, teamWithUsersResources, assigneeResources]);

  return (
    <>
      <ScheduleComponent
        key={resourceModeKey}
        ref={scheduleRef}
        cssClass={`rp-syncfusion-scheduler${workloadEnabled ? ' rp-syncfusion-scheduler--workload' : ''}${(viewMode === 'month-daily' || viewMode === 'year-monthly' || viewMode === 'year-daily') ? ' rp-syncfusion-scheduler--year' : ''}`}
        width="100%"
        height="100%"
        selectedDate={selectedDate}
        currentView={currentViewName as never}
        headerRows={getHeaderRowsForMode(viewMode) as never}
        eventSettings={eventSettings}
        group={groupConfig}
        resourceHeaderTemplate={resourceHeaderTemplate as never}
        actionBegin={onActionBegin}
        actionComplete={onActionComplete}
        eventClick={onEventClick}
        popupOpen={onPopupOpen}
        select={onSelect as never}
        navigating={onNavigating}
        renderCell={onRenderCell}
        eventRendered={onEventRendered}
        dataBound={onDataBound}
        dragStart={onDragStart as never}
        resizeStart={onResizeStart as never}
        resizeStop={onResizeStop as never}
        allowDragAndDrop={true}
        allowResizing={viewMode !== 'year-monthly'}
        showQuickInfo={false}
        rowAutoHeight={true}
        firstDayOfWeek={firstDayOfWeek}
        workDays={hideWeekends ? [1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5, 6]}
        showWeekend={!hideWeekends}
        timeScale={{ enable: false }}
      >
        {resourceDirectives}
        {viewMode === 'year-daily' ? (
          <ViewsDirective>
            <ViewDirective option="TimelineMonth" interval={12} />
          </ViewsDirective>
        ) : viewMode === '2weeks-daily' ? (
          <ViewsDirective>
            <ViewDirective option="TimelineWeek" interval={2} />
          </ViewsDirective>
        ) : (
          <ViewsDirective>
            <ViewDirective option="TimelineWeek" />
            <ViewDirective option="TimelineMonth" />
            <ViewDirective option="TimelineYear" />
          </ViewsDirective>
        )}
        <Inject
          services={[
            TimelineViews,
            TimelineMonth,
            TimelineYear,
            Resize,
            DragAndDrop,
          ]}
        />
      </ScheduleComponent>

      {/* ── Capacity detail popup ────────────────────────────────────────── */}
      {capacityPopup && (
        <CapacityDetailPopup
          userName={capacityPopup.userName}
          aggregatedEntries={capacityPopup.aggregatedEntries}
          displayMode={workloadDisplayMode}
          indicatorType={indicatorType}
          anchorRect={capacityPopup.anchorRect}
          onMouseEnter={cancelHideCapacityPopup}
          onClose={hideCapacityPopup}
        />
      )}

    </>
  );
});

SyncfusionScheduler.displayName = 'SyncfusionScheduler';

export default SyncfusionScheduler;
